import { html, nothing, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import {
  HealthDashboardInsightsData,
  HealthDashboardInsightsStrings,
  InsightsStringsItem,
  InsightsStringsSection,
  InsightsTemplatesStringKeys,
} from '../types';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { WidgetSectionItemSeverity } from '../../../../ui/gzWidgetSectionItem/types';
import { WidgetHeaderItemSize } from '../../../../ui/gzWidgetHeaderItem/types';
import { translateFn } from '../../../../utils/translator.helper';
import { isEmpty } from 'lodash';
import { classMap } from 'lit/directives/class-map.js';

// class will only be extended, not used as a standalone web component/widget
export class GzHealthDashboardInsights extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: HealthDashboardInsightsData;

  protected widgetKey: string;

  protected strings: HealthDashboardInsightsStrings;

  protected changedInfoBoxText: string;

  protected changedInfoBoxHasPadding = false;

  private zeroValuesWithErrorIconsKeys: string[] = [
    InsightsTemplatesStringKeys.UPDATED,
    InsightsTemplatesStringKeys.COMPLIANT,
  ];

  static get styles() {
    return styles;
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="loading-container">
        <div class="loading-header">
          <gz-loader></gz-loader>
          <div class="vertical-bar"></div>
          <gz-loader></gz-loader>
          <div class="vertical-bar"></div>
          <gz-loader></gz-loader>
        </div>
        ${this.strings?.sections.map(section => this.renderSectionLoader(section))}
      </div>
    `;
  }

  protected renderSectionLoader(section: InsightsStringsSection): TemplateResult {
    return html` <div class="loading-section"><gz-loader rows="${section.items.length}"></gz-loader></div>`;
  }

  protected renderContent(): TemplateResult {
    return html`
      ${this.renderHeader()} ${this.renderChangedInfoBox(this.changedInfoBoxText)}
      ${this.strings.sections.map(section => this.renderSection(section))}
    `;
  }

  protected renderHeader(): TemplateResult | undefined {
    const overview = this.payload?.[this.widgetKey]?.['overview'];
    const longNames = this.longHeaderItemStrings();
    return html`
      <div class="header">
        ${this.renderHeaderItem('success', this.getHeaderItemStrings()[0], longNames, overview?.healthy ?? 0)}
        <div class="vertical-bar"></div>
        ${this.renderHeaderItem('warning', this.getHeaderItemStrings()[1], longNames, overview?.warning ?? 0)}
        <div class="vertical-bar"></div>
        ${this.renderHeaderItem('negative', this.getHeaderItemStrings()[2], longNames, overview?.critical ?? 0)}
      </div>
    `;
  }

  protected renderHeaderItem(iconClass: string, name: string, longNames = false, value?: number): TemplateResult {
    return html`
      <gz-widget-header-item
        .name="${name}"
        .value="${value ?? 0}"
        .size="${this.getWidgetHeaderItemSize()}"
        .state="${this.state}"
        .iconClass="ds-icon-notification_${iconClass}"
        ?decreaseNameFont="${longNames}"
      ></gz-widget-header-item>
    `;
  }

  protected renderChangedInfoBox(text?: string): TemplateResult | undefined {
    if (this.state !== WidgetStateFrontEnd.IS_CHANGED) {
      return;
    }
    const classes = { 'changed-info-box': true, 'mac-os-infobox': this.changedInfoBoxHasPadding };
    return html`<div class=${classMap(classes)}>${super.renderChangedInfoBox(text)}</div>`;
  }

  protected renderSection(section: InsightsStringsSection): TemplateResult {
    return html`
      <div class="section">
        ${this.renderSectionTitle(section)} ${section.items.map(item => this.renderSectionItem(section, item))}
      </div>
    `;
  }

  protected renderSectionTitle(section: InsightsStringsSection): TemplateResult | symbol {
    if (!section.title) {
      return nothing;
    }
    const sectionTitle =
      this.state === WidgetStateFrontEnd.IS_ERROR || this.state === WidgetStateFrontEnd.IS_CHANGED
        ? '...'
        : section.title;
    return html`
      <gz-tooltip isEllipsis tooltipText="${sectionTitle}">
        <div class="section-title fs-11">${sectionTitle}</div>
      </gz-tooltip>
    `;
  }

  protected renderSectionItem(section: InsightsStringsSection, item: InsightsStringsItem): TemplateResult {
    const value = !isEmpty(this.payload) ? this.payload[this.widgetKey][section.key][item.key] : 0;
    const severity = this.getSectionItemSeverity(item, value);

    return html`
      <gz-widget-section-item
        .name=${item.name}
        .value=${value}
        .severity=${severity}
        .state=${this.state}
        itemTooltipText="${item.tooltipText}"
      />
    `;
  }

  private getSectionItemSeverity(item: InsightsStringsItem, value: number): string {
    return value === 0 ? this.getSeverityForZeroValues(item.key) : item.severity ?? WidgetSectionItemSeverity.NONE;
  }

  protected getWidgetHeaderItemSize(): WidgetHeaderItemSize {
    if (!this.hasResizeLimits()) {
      if (this.currentWidthInterval[1] === this.resizeBreakingPoints[0]) {
        return WidgetHeaderItemSize.SMALL;
      }
      if (
        this.currentWidthInterval[0] === this.resizeBreakingPoints[0] &&
        this.currentWidthInterval[1] === this.resizeBreakingPoints[1]
      ) {
        return WidgetHeaderItemSize.MEDIUM;
      }
    }
    return WidgetHeaderItemSize.BIG;
  }

  protected getSeverityForZeroValues(key: string): WidgetSectionItemSeverity {
    return this.zeroValuesWithErrorIconsKeys.includes(key)
      ? WidgetSectionItemSeverity.ERROR
      : WidgetSectionItemSeverity.SUCCESS;
  }

  private longHeaderItemStrings(): boolean {
    return this.getHeaderItemStrings().some(item => item.length > 9);
  }

  private getHeaderItemStrings(): string[] {
    return [translateFn('Healthy'), translateFn('Warning'), translateFn('Critical')];
  }
}
